import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';
import queryString from 'query-string';

import { RegistrationQuizStep } from 'types';
import { SimpleDatesUserDetailsKey } from 'types/enums/SimpleDatesUserDetailsKey';

import { UserProfileApi } from 'api/UserProfileApi';
import { useQuizFunnelAnalytics } from 'hooks/analytics/useQuizFunnelAnalytics';
import { useUserOptionsQuery } from 'hooks/queries/useUserOptionsQuery';
import { useMedia } from 'hooks/useMedia';
import {
  QueryKeys,
  useSystemSearchQueries,
} from 'hooks/useSystemSearchQueries';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Popup } from 'components/UI/Popup';

import { Header } from './submodules/Header';
import { MultiSelect } from './submodules/MultiSelect';
import { SingleSelect } from './submodules/SingleSelect';

import css from './registrationQuiz.module.sass';

interface Props {
  value?: string;
}

type SelectedAnswer = {
  name: string;
  value: number[];
};

const transformSelectedDataToObject = (selectedAnswers: SelectedAnswer[]) => {
  return selectedAnswers.reduce(
    (acc, answer) => ({
      ...acc,
      [answer.name]:
        answer.name === SimpleDatesUserDetailsKey.Languages
          ? answer.value
          : answer.value[0],
    }),
    {}
  );
};

export const RegistrationQuiz: React.FC<Props> = () => {
  const { isMobile } = useMedia();
  const history = useHistory();
  const { pathname, search } = useLocation();

  const [isPopupShown, setIsPopupShown] = useState(false);

  const [steps, setSteps] = useState<RegistrationQuizStep[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<SelectedAnswer[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const { getQuizQuery } = useSystemSearchQueries();

  const { value: quizQuery } = getQuizQuery();

  const currentStep = useMemo(() => {
    if (steps.length && currentStepIndex < steps.length) {
      return steps[currentStepIndex];
    }
    return null;
  }, [currentStepIndex, steps]);

  const { trackQuizShow, trackQuizHide, trackQuizSkip, trackQuizAnswer } =
    useQuizFunnelAnalytics();

  const hideQuiz = useCallback(() => {
    setIsPopupShown(false);
    trackQuizHide();
  }, [trackQuizHide]);

  const showQuiz = useCallback(() => {
    setIsPopupShown(true);
    trackQuizShow();
  }, [trackQuizShow]);

  const { userOptionsQuery } = useUserOptionsQuery({
    enabled: !steps.length,
    onError: () => {
      hideQuiz();
    },
    onSuccess: (data) => {
      setSteps(
        [
          {
            name: SimpleDatesUserDetailsKey.LookingFor,
            type: 'select',
            label: 'Why you’re here?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.looking_for?.options,
            optionsSmiles: ['💬', '🫦', '❤️', '😍'],
          },
          {
            name: SimpleDatesUserDetailsKey.MaritalStatus,
            type: 'select',
            label: 'What’s your status?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.marital_status?.options,
            optionsSmiles: ['😀', '❤️', '💔', '😘', '🤐'],
          },
          {
            name: SimpleDatesUserDetailsKey?.HaveKids,
            type: 'select',
            label: 'Do you have kids?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.have_kids?.options,
            optionsSmiles: ['❌', '✅', '❤️‍🩹', '😉'],
          },
          {
            name: SimpleDatesUserDetailsKey.WantChildren,
            type: 'select',
            label: 'Do you want children?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.want_children?.options,
            optionsSmiles: ['❌', '✅', '🤐', '😉'],
          },
          {
            name: SimpleDatesUserDetailsKey.Languages,
            type: 'multiselect',
            label: 'What language do you speak?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.languages?.options,
          },
          {
            name: SimpleDatesUserDetailsKey.BodyType,
            type: 'select',
            label: 'What’s your body type?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.body_type?.options,
            optionsSmiles: ['🧍‍♂️', '😇', '🏋️', '😊', '😋', '🤐'],
          },
          {
            name: SimpleDatesUserDetailsKey.Height,
            type: 'select',
            label: 'How tall are you?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.height?.options,
          },
          {
            name: SimpleDatesUserDetailsKey.Drink,
            type: 'select',
            label: 'Do you drink?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.drink?.options,
            optionsSmiles: ['❌', '🥂', '🍺', '🤷'],
          },
          {
            name: SimpleDatesUserDetailsKey.Smoking,
            type: 'select',
            label: 'Do you smoke?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.smoking?.options,
            optionsSmiles: ['❌', '🤫', '🚬', '🤷'],
          },
          {
            name: SimpleDatesUserDetailsKey.StarSign,
            type: 'select',
            label: 'What’s your star sign?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.star_sign?.options,
            optionsSmiles: [
              '♈️',
              '♉️',
              '♊️',
              '♋️',
              '♌️',
              '♍️',
              '♎️',
              '♏️',
              '♐️',
              '♑',
              '️♒️',
              '♓️',
              '🤐',
            ],
          },
          {
            name: SimpleDatesUserDetailsKey.Education,
            type: 'select',
            label: 'What level of education do you have?',
            description: 'Increase compatibility by sharing yours!',
            value: '',
            active: true,
            options: data.education?.options,
            optionsSmiles: ['📖️', '📚', '⛪️️', '🎓️', '📜️', '🏔️', '🤷️'],
          },
        ].filter((item) => !!item.options)
      );
    },
  });

  const handleSingleSelectChange = useCallback(
    (name: string, value: number) => {
      setSelectedAnswers((prev) => {
        // ? answer wasn't selected
        if (!prev.some((answer: SelectedAnswer) => answer.name === name)) {
          return [...prev, { name, value: [value] }];
        }

        // ? answer was selected
        return [
          ...prev.map((answer) => {
            if (answer.name === name) {
              return { ...answer, value: [value] };
            }
            return answer;
          }),
        ];
      });
    },
    []
  );

  const handleMultiSelectChange = useCallback((name: string, value: number) => {
    setSelectedAnswers((prev) => {
      // ? answer wasn't selected
      if (!prev.some((answer: SelectedAnswer) => answer.name === name)) {
        return [...prev, { name, value: [value] }];
      }

      // ? answer was selected
      return [
        ...prev.map((answer) => {
          if (answer.name === name) {
            if (answer.value.includes(value)) {
              return {
                ...answer,
                value: answer.value.filter(
                  (selectedValue) => selectedValue !== value
                ),
              };
            }

            return { ...answer, value: [...answer.value, value] };
          }
          return answer;
        }),
      ];
    });
  }, []);

  const removeQuizFromParams = useCallback(() => {
    const params = queryString.parse(search);
    delete params[QueryKeys.Quiz];

    history.replace({
      pathname,
      search: queryString.stringify(params),
    });
  }, [history, pathname, search]);

  const handleNextBtnClick = useCallback(() => {
    const updatedAnswer = selectedAnswers.find(
      (answer) => answer.name === currentStep?.name
    );

    if (updatedAnswer?.value?.length) {
      trackQuizAnswer({ step: currentStepIndex + 1, answer: updatedAnswer });
    }

    if (currentStepIndex === steps.length - 1) {
      const dataToSave = transformSelectedDataToObject(selectedAnswers);

      UserProfileApi.editUserInfo({
        details: dataToSave,
      });

      removeQuizFromParams();
      hideQuiz();
      return;
    }

    if (updatedAnswer?.value?.length) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  }, [
    currentStep?.name,
    currentStepIndex,
    hideQuiz,
    removeQuizFromParams,
    selectedAnswers,
    steps.length,
    trackQuizAnswer,
  ]);

  const handleSkipBtnClick = useCallback(() => {
    trackQuizSkip({ step: currentStepIndex + 1 });

    if (currentStepIndex === steps.length - 1) {
      const dataToSave = transformSelectedDataToObject(selectedAnswers);

      UserProfileApi.editUserInfo({
        details: dataToSave,
      });

      removeQuizFromParams();
      hideQuiz();
      return;
    }

    setCurrentStepIndex((prev) => prev + 1);
  }, [
    currentStepIndex,
    hideQuiz,
    removeQuizFromParams,
    selectedAnswers,
    steps.length,
    trackQuizSkip,
  ]);

  const handleBackBtnClick = () => {
    setCurrentStepIndex((prev) => prev - 1);
  };

  useEffect(() => {
    if (quizQuery) {
      showQuiz();
    }
  }, [quizQuery, showQuiz]);

  return (
    <>
      {isPopupShown && (
        <Popup
          isFullScreen={isMobile}
          isIndented={false}
          popupClassName={cx({ [css.root]: !isMobile })}
          bodyClassName={css.rootBody}
        >
          <Header
            currentStep={currentStepIndex}
            totalSteps={steps.length}
            onBackBtnClick={handleBackBtnClick}
          />
          <div className={css.main}>
            {!!currentStep?.label && (
              <h2 className={css.question}>{currentStep.label}</h2>
            )}
            {!!currentStep?.description && (
              <p className={css.hint}>{currentStep.description}</p>
            )}
            <div className={css.options}>
              {!!currentStep?.options?.length &&
                currentStep.type === 'select' && (
                  <SingleSelect
                    options={currentStep.options}
                    optionsSmiles={currentStep?.optionsSmiles}
                    currentStep={currentStep}
                    selectedAnswers={
                      selectedAnswers.find(
                        (answer) => answer.name === currentStep.name
                      )?.value || []
                    }
                    handleChange={handleSingleSelectChange}
                  />
                )}
              {!!currentStep?.options?.length &&
                currentStep.type !== 'select' && (
                  <MultiSelect
                    options={currentStep.options}
                    currentStep={currentStep}
                    selectedAnswers={
                      selectedAnswers.find(
                        (answer) => answer.name === currentStep.name
                      )?.value || []
                    }
                    handleChange={handleMultiSelectChange}
                  />
                )}
            </div>
            <div className={css.btns}>
              <BaseButton
                type={ButtonTypes.Regular}
                className={css.btn}
                disabled={userOptionsQuery.isLoading}
                onClick={handleSkipBtnClick}
              >
                Skip
              </BaseButton>
              <BaseButton
                type={ButtonTypes.Primary}
                className={css.btn}
                disabled={userOptionsQuery.isLoading}
                onClick={handleNextBtnClick}
              >
                Next
              </BaseButton>
            </div>
          </div>
        </Popup>
      )}
    </>
  );
};
